// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// material ui
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';

// feature-components
import CopyableIdField from '@/features/devices/components/shared/CopyableIdField';
import DetailHeaderCard from '@/features/devices/components/shared/DetailHeaderCard';
import DetailInfoField from '@/features/devices/components/shared/DetailInfoField';
import useCopyToClipboard from '@/features/devices/components/shared/hooks/useCopyToClipboard';

const UnitDetailHeader = ({ unit }) => {
  const { t } = useTranslation('units-gateways-sensors');
  const copyToClipboard = useCopyToClipboard();

  const handleCopyUnitId = () =>
    copyToClipboard(
      unit?.UnitId,
      t?.('units.unitIdCopied', { id: unit.UnitId }) ||
        `Unit ID copied: ${unit.UnitId}`,
      t?.('units.unitIdCopyFailed') || 'Failed to copy Unit ID',
    );

  return (
    <DetailHeaderCard
      title={unit?.Name || t('common.notAvailable')}
      subheader={unit?.Description}
      avatar={<LightRoundedIcon fontSize="medium" />}
      action={
        <Tooltip
          title={t('units.viewData', {
            name: unit?.Name || unit?.UnitId || '',
          })}
        >
          <IconButton
            component={Link}
            to={`/$accountId/view-data?units=${unit?.UnitId}`}
            sx={{ borderRadius: 2 }}
            size="small"
          >
            <BarChartRoundedIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <DetailInfoField label={t('units.unitId')}>
        <CopyableIdField
          value={unit?.UnitId}
          notAvailableText={t('common.notAvailable')}
          onCopy={handleCopyUnitId}
        />
      </DetailInfoField>

      <DetailInfoField label={t('units.type')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {unit?.UnitType?.Name || t('common.notAvailable')}
        </Typography>
      </DetailInfoField>

      <DetailInfoField label={t('units.tags')}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {unit?.Tags && unit.Tags.length > 0 ? (
            unit.Tags.map((tag, index) => (
              <Chip
                icon={<LocalOfferIcon fontSize="small" color="white" />}
                size="small"
                key={index}
                label={tag.Text}
                sx={{
                  borderRadius: 1,
                  backgroundColor: 'info.main',
                  color: 'white',
                }}
              />
            ))
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('units.noTagsForUnit')}
            </Typography>
          )}
        </Stack>
      </DetailInfoField>
    </DetailHeaderCard>
  );
};

export default UnitDetailHeader;
